import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Center,
  Group,
  Image,
  Loader,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { IconCircleFilled, IconCircle, IconX } from "@tabler/icons-react";
import {
  LinkedSource,
  LinkedSourceSubsite,
  organMapTemplatesService,
} from "../../../services/admin/organMapTemplates.service";

interface PositionEditorProps {
  imageUrl: string | null;
  width: number | null;
  height: number | null;
  linkedSources: LinkedSource[];
  onPositionUpdate: (
    subsiteId: string,
    positionX: number | null,
    positionY: number | null
  ) => void;
}

export default function PositionEditor({
  imageUrl,
  width,
  height,
  linkedSources,
  onPositionUpdate,
}: PositionEditorProps) {
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [selectedSubsiteId, setSelectedSubsiteId] = useState<string | null>(
    null
  );
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Resolve S3 key to download URL
  useEffect(() => {
    if (imageUrl && !imageUrl.startsWith("http")) {
      setLoadingImage(true);
      organMapTemplatesService
        .getDownloadUrl(imageUrl)
        .then((res) => setResolvedImageUrl(res.url))
        .catch(() => setResolvedImageUrl(null))
        .finally(() => setLoadingImage(false));
    } else {
      setResolvedImageUrl(imageUrl);
    }
  }, [imageUrl]);

  // Get all subsites flat list
  const allSubsites: (LinkedSourceSubsite & { sourceName: string })[] =
    linkedSources.flatMap((source) =>
      source.subsites.map((sub) => ({ ...sub, sourceName: source.name }))
    );

  const selectedSubsite = allSubsites.find((s) => s.id === selectedSubsiteId);

  const handleImageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!selectedSubsiteId) return;

      const container = imageContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      // Store as percentage (0-100) of image dimensions for resolution independence
      const xPercent = parseFloat(
        (((e.clientX - rect.left) / rect.width) * 100).toFixed(2)
      );
      const yPercent = parseFloat(
        (((e.clientY - rect.top) / rect.height) * 100).toFixed(2)
      );

      onPositionUpdate(selectedSubsiteId, xPercent, yPercent);
    },
    [selectedSubsiteId, onPositionUpdate]
  );

  const handleClearPosition = (subsiteId: string) => {
    onPositionUpdate(subsiteId, null, null);
  };

  // Calculate display dimensions (max 500px wide)
  const imgWidth = width || 500;
  const imgHeight = height || 400;
  const maxDisplayWidth = 500;
  const scale = Math.min(1, maxDisplayWidth / imgWidth);
  const displayWidth = Math.round(imgWidth * scale);
  const displayHeight = Math.round(imgHeight * scale);

  if (!imageUrl) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        Upload a template image first to use the position editor.
      </Text>
    );
  }

  if (linkedSources.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        Link sources to this template first to position subsites.
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      <Text size="sm" fw={600}>
        Subsite Position Editor
      </Text>
      <Text size="xs" c="dimmed">
        Select a subsite from the list, then click on the diagram to set its
        position. Click a positioned subsite marker to reposition it.
      </Text>

      <Group
        align="flex-start"
        gap="md"
        wrap="nowrap"
        style={{ maxWidth: "100%" }}
      >
        {/* Image area */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          {loadingImage ? (
            <Center w={displayWidth} h={displayHeight}>
              <Loader size="sm" />
            </Center>
          ) : (
            <Box
              ref={imageContainerRef}
              pos="relative"
              w={displayWidth}
              h={displayHeight}
              style={{
                cursor: selectedSubsiteId ? "crosshair" : "default",
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: 4,
                overflow: "hidden",
              }}
              onClick={handleImageClick}
            >
              <Image
                src={resolvedImageUrl}
                alt="Organ map template"
                w={displayWidth}
                h={displayHeight}
                fit="contain"
              />

              {/* Render positioned markers */}
              {allSubsites
                .filter((s) => s.positionX !== null && s.positionY !== null)
                .map((subsite) => (
                  <Tooltip
                    key={subsite.id}
                    label={`${subsite.name} (${subsite.sourceName})`}
                  >
                    <Box
                      pos="absolute"
                      w={12}
                      h={12}
                      style={{
                        left: `calc(${subsite.positionX}% - 6px)`,
                        top: `calc(${subsite.positionY}% - 6px)`,
                        borderRadius: "50%",
                        backgroundColor:
                          subsite.id === selectedSubsiteId
                            ? "var(--mantine-color-blue-6)"
                            : "var(--mantine-color-red-6)",
                        border: "2px solid white",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                        cursor: "pointer",
                        zIndex: subsite.id === selectedSubsiteId ? 10 : 5,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSubsiteId(subsite.id);
                      }}
                    />
                  </Tooltip>
                ))}
            </Box>
          )}

          {selectedSubsite && (
            <Text size="xs" c="blue" mt={4}>
              Selected: <strong>{selectedSubsite.name}</strong> — Click on
              diagram to set position
            </Text>
          )}
        </Box>

        {/* Subsites list */}
        <ScrollArea
          h={displayHeight + 30}
          offsetScrollbars
          style={{ flex: 1, minWidth: 0 }}
        >
          <Stack gap={4}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Subsites to Position
            </Text>
            {linkedSources.map((source) => (
              <Box key={source.id}>
                <Text size="xs" fw={600} mt="xs" mb={2}>
                  {source.name}
                </Text>
                {source.subsites.length === 0 ? (
                  <Text size="xs" c="dimmed" pl="xs">
                    No subsites
                  </Text>
                ) : (
                  source.subsites.map((subsite) => {
                    const hasPosition =
                      subsite.positionX !== null && subsite.positionY !== null;
                    const isSelected = selectedSubsiteId === subsite.id;

                    return (
                      <Group
                        key={subsite.id}
                        gap={4}
                        wrap="nowrap"
                        py={2}
                        px="xs"
                        style={{
                          borderRadius: 4,
                          backgroundColor: isSelected
                            ? "var(--mantine-color-blue-0)"
                            : "transparent",
                          cursor: "pointer",
                        }}
                      >
                        <UnstyledButton
                          onClick={() => setSelectedSubsiteId(subsite.id)}
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          {hasPosition ? (
                            <IconCircleFilled
                              size={12}
                              color="var(--mantine-color-green-6)"
                            />
                          ) : (
                            <IconCircle
                              size={12}
                              color="var(--mantine-color-gray-4)"
                            />
                          )}
                          <Text size="xs" style={{ flex: 1 }}>
                            {subsite.name}
                          </Text>
                          {hasPosition && (
                            <Text size="xs" c="dimmed">
                              ({subsite.positionX}%, {subsite.positionY}%)
                            </Text>
                          )}
                        </UnstyledButton>

                        {hasPosition && (
                          <Tooltip label="Clear position">
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="red"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClearPosition(subsite.id);
                              }}
                            >
                              <IconX size={10} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    );
                  })
                )}
              </Box>
            ))}
          </Stack>
        </ScrollArea>
      </Group>
    </Stack>
  );
}
